import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Bell, BellOff, Mail, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreference,
} from "@/services/notificationPreferencesService";

const CATEGORY_LABELS: Record<string, { es: string; en: string }> = {
  account_security: {
    es: "Cuenta y seguridad",
    en: "Account & Security",
  },
  invitations: {
    es: "Invitaciones",
    en: "Invitations",
  },
  request_updates: {
    es: "Actualizaciones de gestiones",
    en: "Request Updates",
  },
  onboarding: {
    es: "Onboarding y configuración",
    en: "Onboarding & Setup",
  },
  payments: {
    es: "Pagos",
    en: "Payments",
  },
  support: {
    es: "Soporte y feedback",
    en: "Support & Feedback",
  },
};

const CATEGORY_DESCRIPTIONS: Record<string, { es: string; en: string }> = {
  account_security: {
    es: "Links de inicio de sesión, códigos de verificación y alertas de seguridad. Siempre activas.",
    en: "Login links, verification codes, and security alerts. Always active.",
  },
  invitations: {
    es: "Invitaciones a la plataforma y a empresas.",
    en: "Platform and company invitations.",
  },
  request_updates: {
    es: "Estado de gestiones notariales, trabajos y recordatorios.",
    en: "Notarial request status, certificates, and reminders.",
  },
  onboarding: {
    es: "Revisiones de cuenta, aprobaciones y configuración inicial.",
    en: "Account reviews, approvals, and initial setup.",
  },
  payments: {
    es: "Facturas, cancelaciones y solicitudes de pago.",
    en: "Invoices, cancellations, and payout requests.",
  },
  support: {
    es: "Feedback, solicitudes de soporte y mensajes.",
    en: "Feedback, support requests, and messages.",
  },
};

function getCategoryLabel(slug: string, lang: string): string {
  const entry = CATEGORY_LABELS[slug];
  if (!entry) return slug;
  return lang === "en" ? entry.en : entry.es;
}

function getCategoryDescription(slug: string, lang: string): string {
  const entry = CATEGORY_DESCRIPTIONS[slug];
  if (!entry) return "";
  return lang === "en" ? entry.en : entry.es;
}

export function NotificationSettingsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: getNotificationPreferences,
  });

  const mutation = useMutation({
    mutationFn: (prefs: { category: string; email?: boolean; push?: boolean }[]) => {
      const promise = updateNotificationPreferences(prefs);
      void toast.promise(promise, {
        loading: lang === "en" ? "Saving..." : "Guardando...",
        success: lang === "en" ? "Preferences updated" : "Preferencias actualizadas",
        error: lang === "en" ? "Could not update preferences. Try again." : "No se pudieron actualizar las preferencias. Intentá de nuevo.",
      });
      return promise;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
    },
  });

  const dismissable = useMemo(
    () => preferences?.filter((p) => p.dismissable) ?? [],
    [preferences],
  );

  const allEmailOn = dismissable.length > 0 && dismissable.every((p) => p.email);
  const allPushOn = dismissable.length > 0 && dismissable.every((p) => p.push);
  const allOn = allEmailOn && allPushOn;

  function handleToggle(
    pref: NotificationPreference,
    channel: "email" | "push",
    checked: boolean,
  ) {
    mutation.mutate([{ category: pref.category, [channel]: checked }]);
  }

  function handleMasterToggle(channel: "email" | "push" | "all", checked: boolean) {
    const updates = dismissable.map((p) => {
      if (channel === "all") {
        return { category: p.category, email: checked, push: checked };
      }
      return { category: p.category, [channel]: checked };
    });
    mutation.mutate(updates);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 md:p-8">
      <PageHeader
        title={
          lang === "en"
            ? "Notification preferences"
            : "Preferencias de notificaciones"
        }
        description={
          lang === "en"
            ? "Choose how you want to be notified"
            : "Elegí cómo querés recibir notificaciones"
        }
        actions={
          <Button variant="outline" asChild>
            <Link to="/profile" className="inline-flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("profile.title", "Profile")}
            </Link>
          </Button>
        }
      />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-8">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {lang === "en"
              ? "Could not load notification preferences."
              : "No se pudieron cargar las preferencias de notificaciones."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  {allOn ? (
                    <Bell className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <BellOff className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Label htmlFor="master-all" className="text-sm font-medium">
                    {lang === "en" ? "All notifications" : "Todas"}
                  </Label>
                  <Switch
                    id="master-all"
                    checked={allOn}
                    disabled={mutation.isPending}
                    onCheckedChange={(checked) => handleMasterToggle("all", checked)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="master-email" className="text-sm font-medium">
                    {lang === "en" ? "All email" : "Todos los emails"}
                  </Label>
                  <Switch
                    id="master-email"
                    checked={allEmailOn}
                    disabled={mutation.isPending}
                    onCheckedChange={(checked) => handleMasterToggle("email", checked)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="master-push" className="text-sm font-medium">
                    {lang === "en" ? "All push" : "Todos los push"}
                  </Label>
                  <Switch
                    id="master-push"
                    checked={allPushOn}
                    disabled={mutation.isPending}
                    onCheckedChange={(checked) => handleMasterToggle("push", checked)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {preferences?.map((pref) => (
            <Card key={pref.category}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  {getCategoryLabel(pref.category, lang)}
                </CardTitle>
                <CardDescription className="text-xs">
                  {getCategoryDescription(pref.category, lang)}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <Label
                      htmlFor={`email-${pref.category}`}
                      className="text-sm"
                    >
                      Email
                    </Label>
                    <Switch
                      id={`email-${pref.category}`}
                      checked={pref.email}
                      disabled={!pref.dismissable || mutation.isPending}
                      onCheckedChange={(checked) =>
                        handleToggle(pref, "email", checked)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    <Label
                      htmlFor={`push-${pref.category}`}
                      className="text-sm"
                    >
                      Push
                    </Label>
                    <Switch
                      id={`push-${pref.category}`}
                      checked={pref.push}
                      disabled={!pref.dismissable || mutation.isPending}
                      onCheckedChange={(checked) =>
                        handleToggle(pref, "push", checked)
                      }
                    />
                  </div>
                  {!pref.dismissable && (
                    <span className="text-xs text-muted-foreground">
                      {lang === "en" ? "Always on" : "Siempre activas"}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
